import { useDispatch, useSelector } from 'react-redux';
import State from 'types/State';
import {
  firstUserleadSubmitted,
  setQuickContactState,
  enableQuickContact,
  resetQuickContact,
} from 'redux-implementations/quickContact';
import { useEffect } from 'react';
import { useAutolistUser } from './useAutolistUser';

export function useQuickContactLifecycle(): void {
  const { id: userId } = useAutolistUser();
  const dispatch = useDispatch();
  useEffect(() => {
    if (userId) {
      const quickContactLocalStorage = new QuickContactLocalStorage(userId);
      const isQuickContactEnabled = quickContactLocalStorage.isQuickContactEnabled();
      const isFirstLeadSubmitted = quickContactLocalStorage.isFirstLeadSubmitted();
      dispatch(
        setQuickContactState({
          quickContactEnabled: isQuickContactEnabled,
          firstUserLeadSubmitted: isFirstLeadSubmitted,
        })
      );
    } else {
      dispatch(resetQuickContact());
    }
  }, [userId, dispatch]);
}

export function useSetQuickContact(): () => void {
  const dispatch = useDispatch();
  const isFirstLeadSubmitted = useIsFirstUserLeadSubmitted();
  const quickContactEnabled = useIsQuickContactEnabled();
  const { id: userId } = useAutolistUser();

  return function setQuickContact(): void {
    if (userId) {
      const quickContactLocalStorage = new QuickContactLocalStorage(userId);
      if (!quickContactEnabled && isFirstLeadSubmitted) {
        dispatch(enableQuickContact());
        quickContactLocalStorage.enableQuickContact();
      } else {
        dispatch(firstUserleadSubmitted(true));
        quickContactLocalStorage.setFirstLeadSubmitted();
      }
    }
  };
}

export function useIsQuickContactEnabled(): boolean {
  return useSelector((state: State) => state.quickContact.quickContactEnabled);
}

export function useIsFirstUserLeadSubmitted(): boolean {
  return useSelector((state: State) => state.quickContact.firstUserLeadSubmitted);
}

enum QuickContactKey {
  QUICK_CONTACT_ENABLED = 'quickContactEnabled',
  FIRST_USER_LEAD_SUBMITTED = 'firstUserLeadSubmitted',
}

class QuickContactLocalStorage {
  private readonly enabledKey: string;

  private readonly firstLeadKey: string;

  constructor(userId: number) {
    this.enabledKey = `${QuickContactKey.QUICK_CONTACT_ENABLED}_${userId}`;
    this.firstLeadKey = `${QuickContactKey.FIRST_USER_LEAD_SUBMITTED}_${userId}`;
  }

  isQuickContactEnabled(): boolean {
    return localStorage.getItem(this.enabledKey) === 'true';
  }

  enableQuickContact(): void {
    localStorage.setItem(this.enabledKey, 'true');
  }

  isFirstLeadSubmitted(): boolean {
    return localStorage.getItem(this.firstLeadKey) === 'true';
  }

  setFirstLeadSubmitted(): void {
    localStorage.setItem(this.firstLeadKey, 'true');
  }
}
